import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { CalculationReviewPanel } from './';
import { EngineDetailView } from './EngineDetailViews';
import PLReconciliationGrid from '../PLReconciliationGrid';
import MultiSourceReconciliation from '../MultiSourceReconciliation';
import { api } from '../../../utils/api';
import {
  ENGINE_NAMES,
  type EngineId,
  type ReviewPanelMode,
  type OverrideSuggestion,
} from '../../../types/engineReview';

export interface EngineReviewDetailContentProps {
  mode: ReviewPanelMode;
  reportId?: string;
  engineId?: string;
  backHref?: string;
}

const VALID_ENGINES = new Set<string>(Object.keys(ENGINE_NAMES));

// ---------------------------------------------------------------------------
// Merchant Reconciliation — proof-of-concept engine card
// Reuses the same fetch + components as AccountantReconciliation.tsx
// ---------------------------------------------------------------------------

interface PLGridData {
  report_id: string;
  years: number[];
  pl_structure: any;
  pl_grid: any;
  reconciliation_status: Record<string, string>;
  catch_all_items: Array<[string, string]>;
}

function MerchantReconciliationCard({
  reportId,
  refreshKey = 0,
}: {
  reportId: string;
  refreshKey?: number;
}) {
  const [plGridData, setPlGridData] = useState<PLGridData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      setNotFound(false);
      try {
        const data = await api.get(`/api/v1/reconciliation/pl-grid/${reportId}`);
        setPlGridData(data as PLGridData);
      } catch (err: any) {
        if (err.status === 404) {
          setNotFound(true);
        } else {
          setError(err.message || 'Failed to load reconciliation data');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // refreshKey bumps when the parent panel records a state transition,
    // forcing a re-fetch so the user sees post-action data without a manual
    // page reload.
  }, [reportId, refreshKey]);

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 w-48 rounded" style={{ background: 'var(--color-bg-elevated)' }} />
        <div className="h-64 rounded" style={{ background: 'var(--color-bg-hover)' }} />
        <div className="h-48 rounded" style={{ background: 'var(--color-bg-hover)' }} />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="cs-body rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-muted)' }}>
        No reconciliation grid is available for this case yet.
      </div>
    );
  }

  if (error) {
    return (
      <div className="cs-body rounded-lg p-4" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
        {error}
      </div>
    );
  }

  const handleCellClick = () => {
    // Drill-down deferred to full reconciliation view
  };

  return (
    <div className="space-y-6">
      {plGridData && (
        <div>
          <h3 className="cs-h3 mb-3">
            P&L Reconciliation Grid
          </h3>
          <PLReconciliationGrid
            plGridData={plGridData}
            onCellClick={handleCellClick}
          />
        </div>
      )}
      <div>
        <h3 className="cs-h3 mb-3">
          Multi-Source Comparison
        </h3>
        <MultiSourceReconciliation reportId={reportId} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail page content
// ---------------------------------------------------------------------------

export function EngineReviewDetailContent({
  mode,
  reportId: reportIdProp,
  engineId: engineIdProp,
  backHref: backHrefProp,
}: EngineReviewDetailContentProps) {
  const { reportId: routeReportId, engineId: routeEngineId } = useParams<{ reportId: string; engineId: string }>();
  const reportId = reportIdProp ?? routeReportId;
  const engineId = engineIdProp ?? routeEngineId;
  const navigate = useNavigate();
  const location = useLocation();
  // Bump on every successful state transition so the merchant card refetches
  // its underlying P&L grid. Without this, approve/dispute/waive flips the
  // status pill but the rendered grid stays stale until manual reload.
  const [transitionTick, setTransitionTick] = useState(0);

  // Forensic Assistant deep-link. The chat (or the landing forward) routes
  // here with `location.state.overrideSuggestion`. We capture it ONCE into
  // local state and immediately clear the history entry's state so a refresh
  // or back-navigation doesn't re-open the modal. The captured suggestion is
  // handed to CalculationReviewPanel, which auto-opens the override modal
  // pre-filled + provenance-tagged.
  const [overrideSuggestion, setOverrideSuggestion] =
    useState<OverrideSuggestion | null>(null);

  useEffect(() => {
    const suggestion = (location.state as any)?.overrideSuggestion as
      | OverrideSuggestion
      | undefined;
    // Only honor a suggestion that targets the engine this page is rendering,
    // and only in the edit mode that actually mounts the override modal.
    if (
      suggestion &&
      mode === 'in_house_edit' &&
      suggestion.engineId === engineId
    ) {
      setOverrideSuggestion(suggestion);
    }
    if (suggestion) {
      // Consume the state so a refresh doesn't re-trigger the modal.
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, engineId, mode]);

  if (!reportId || !engineId || !VALID_ENGINES.has(engineId)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg-base)' }}>
        <div className="text-center">
          <p className="cs-meta">
            {!reportId ? 'No report ID provided' : `Unknown engine: ${engineId}`}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="cs-meta mt-3 underline transition-colors"
            style={{ color: 'var(--color-needs-human-text)' }}
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  const validEngineId = engineId as EngineId;

  const backPath = mode === 'external'
    ? `/accountant/review/${reportId}`
    : `/dashboard/accountant/review/${reportId}`;

  const backHref = backHrefProp ?? backPath;

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg-base)' }} data-testid="engine-review-detail">
      {/* Header */}
      <div className="px-6 py-4" style={{ background: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border)' }}>
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(backHref)}
            className="cs-meta flex items-center gap-2 mb-3 transition-colors"
            style={{ color: 'var(--color-text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-text-secondary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; }}
          >
            <ArrowLeft className="w-4 h-4" />
            All Engines
          </button>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h1 className="cs-h2">
              {ENGINE_NAMES[validEngineId]}
            </h1>
            {mode === 'in_house_readonly' && (
              <button
                onClick={() =>
                  navigate(
                    `/dashboard/accountant/review/${reportId}/${validEngineId}/edit`,
                  )
                }
                className="cs-chip inline-flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors"
                style={{ color: 'var(--color-on-brand)', background: 'var(--color-brand)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                data-testid="enter-edit-mode"
              >
                Review this engine
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Review Panel */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <CalculationReviewPanel
          reportId={reportId}
          engineId={validEngineId}
          mode={mode}
          overrideSuggestion={
            overrideSuggestion?.engineId === validEngineId
              ? overrideSuggestion
              : null
          }
          onStateChange={() => setTransitionTick((t) => t + 1)}
        >
          {validEngineId === 'merchant_reconciliation' ? (
            <MerchantReconciliationCard
              reportId={reportId}
              refreshKey={transitionTick}
            />
          ) : (
            // Phase 07.3c: refreshKey threads through EngineDetailView →
            // useEngineReportData so all 11 routed engines re-fetch on every
            // approve/dispute/waive/start-review without a manual reload.
            //
            // TS-FU12: `mode` threads through too so the data hook picks the
            // right namespace (Clerk vs witness JWT). Pre-fix, every engine
            // hit the witness-only endpoint regardless of mode.
            <EngineDetailView
              engineId={validEngineId}
              reportId={reportId}
              refreshKey={transitionTick}
              mode={mode}
            />
          )}
        </CalculationReviewPanel>
      </div>
    </div>
  );
}


export default EngineReviewDetailContent;
